// Reusable navigation group building blocks, lifted verbatim from the old
// getRoutes() in personal-account-navigation.config.tsx — SAME i18n keys, SAME
// paths, SAME icons. The inline isFlow/isBallee ternaries are gone: brand
// filtering now happens in the resolver (resolve-roles.ts) via the existing
// isRouteVisibleForBrand gate (route-brand.config.ts). No parallel brand list
// (Critical Rule #8).
//
// Per-item gates that are NOT brand decisions (feature flags, route visibility
// for sub-routes, billing/team modifiers) stay here as factory inputs so the
// matrix cells remain plain ordered lists.

import {
  Activity,
  AlertTriangle,
  BarChart3,
  Bell,
  Bookmark,
  Building2,
  Calendar,
  CalendarDays,
  CalendarPlus,
  ClipboardCheck,
  Clock,
  CreditCard,
  Eye,
  FileCheck,
  FileText,
  Gift,
  Home,
  Images,
  Inbox,
  KeyRound,
  LayoutGrid,
  Megaphone,
  MessageSquare,
  Package,
  Receipt,
  Scale,
  Settings2,
  Star,
  Store,
  Trophy,
  UserCircle2,
  Users,
  UsersRound,
  Video,
  Wallet,
} from 'lucide-react';

import { dancerPath } from '@kit/shared/handles';

import featureFlagsConfig from '~/config/feature-flags.config';
import pathsConfig from '~/config/paths.config';
import { isRouteVisibleForBrand } from '~/config/route-brand.config';
import type { Brand } from '~/lib/brand';
import type {
  NavGroupDef,
  NavItemDef,
  NavSection,
  PartnerCompetitionRef,
} from './nav-types';

/** Modifiers the resolver feeds into the section factories. */
export interface SectionModifiers {
  isSuperAdmin: boolean;
  isClientUser: boolean;
  isDancer: boolean;
  /** Member of an organization account (drives the `organizer` cell). */
  isOrganizer: boolean;
  /** Has ≥1 marketplace listing; drives the dancer "Selling" group. */
  isSeller: boolean;
  /** Has ≥1 artist_performance_scores row; drives the "Performance" item. */
  hasPerformanceData: boolean;
  /**
   * The reimbursement module is on for at least one of the dancer's clients
   * (or the dancer has no client yet). Drives the Money "Reimbursements" item.
   * Absent means on: no row in the module log means enabled.
   */
  hasReimbursements?: boolean;
  /**
   * The dancer-invoicing module is on for at least one of the dancer's clients
   * (or the dancer has no client yet). Drives the Money "Invoices" item.
   * Absent means on.
   */
  hasDancerInvoicing?: boolean;
  clientRole: 'owner' | 'member' | null;
  /** True when billing should be shown (computed by the resolver). */
  canSeeBilling: boolean;
  /** Competitions the user directs; drives the Ballee "Competitions" group. */
  partnerCompetitions: PartnerCompetitionRef[];
  /** The dancer's public-profile handle (drives the "View my profile" item). */
  dancerProfileSlug: string | null;
}

const group = (def: NavGroupDef): NavSection => ({ kind: 'group', ...def });

/**
 * Competitions the signed-in user directs. One leaf per competition, linking
 * straight to its partner portal (`/competitions/<slug>/partners`). Returns
 * `null` when the user directs nothing, so cells can spread it conditionally.
 * Ballee surface only (competition/partner is a Ballee concept).
 */
export function competitionsPartnerBallee(
  m: SectionModifiers,
): NavSection | null {
  const competitions = m.partnerCompetitions ?? [];
  if (competitions.length === 0) return null;

  return group({
    i18nKey: 'common:routes.competitions',
    icon: Trophy,
    collapsible: true,
    defaultOpen: true,
    items: competitions.map((c) => ({
      i18nKey: c.title,
      path: `/competitions/${c.slug}/partners`,
      icon: Trophy,
    })),
  });
}

/**
 * Offers — the single shared "My Offers" leaf (scholarship/audition offers),
 * consumed by BOTH brands' dancer surfaces (BAL-692). Previously lived only
 * inside `dancerOpportunitiesBallee`, doubly brand-gated with
 * `route-brand.config.ts` scoping `/home/offers` to `'ballee'` — Flow-brand
 * dancers (the competition-participant population) never saw it. One
 * definition, two consumers (Critical Rule #8): `dancerOpportunitiesBallee`
 * below and `dancerPipelineFlow`.
 */
export const offersItem: NavItemDef = {
  i18nKey: 'common:routes.offers',
  path: '/home/offers',
  icon: Gift,
};

export const dividerSection: NavSection = { kind: 'divider' };
export const favoritesSection: NavSection = { kind: 'favorites' };
export const adminToolsSection: NavSection = {
  kind: 'adminTools',
  defaultOpen: false,
};

// ── DANCER · FLOW ───────────────────────────────────────────────────────────

/** Today — Right Now zone surfaces (Flow). Home is NOT here: it leads the
 *  sidebar in the shared primary cluster (Home + Inbox) built by the matrix, so
 *  no cell buries it inside the first labeled group (2026-07-13 top-cluster
 *  restructure — the lone-Inbox fix). */
export const dancerTodayFlow: NavSection = group({
  i18nKey: 'common:routes.today',
  collapsible: true,
  defaultOpen: true,
  items: [
    {
      i18nKey: 'common:routes.assignments',
      path: '/home/assignments',
      icon: ClipboardCheck,
    },
    {
      i18nKey: 'common:routes.schedule',
      path: '/home/schedule',
      icon: CalendarDays,
    },
  ],
});

/** Pipeline — opportunities the dancer can act on (Flow). "My Applications"
 *  is the dancer's own slice of the auditions collection — an L2 affordance
 *  on /auditions, not a sidebar sibling (navigation-architecture skill).
 *  Carries the shared `offersItem` leaf (BAL-692) — Flow-brand dancers are
 *  the competition-participant population and were never shown "My Offers"
 *  before this fix. */
export function dancerPipelineFlow(m: SectionModifiers): NavSection {
  const items: NavItemDef[] = [
    {
      i18nKey: 'common:routes.events',
      path: '/home/events',
      icon: Calendar,
      primary: 3,
    },
    {
      i18nKey: 'common:routes.feedback',
      path: '/home/feedback',
      icon: MessageSquare,
    },
    offersItem,
    {
      i18nKey: 'common:routes.marketplace',
      path: '/marketplace',
      icon: Store,
      isNew: true,
      primary: 2,
    },
  ];
  void m;
  return group({
    i18nKey: 'common:routes.pipeline',
    collapsible: true,
    defaultOpen: true,
    items,
  });
}

/**
 * Money: invoices, reimbursements and payouts (Flow). The Invoices and
 * Reimbursements entries follow the per-client modules (BAL-1160); payouts
 * are not a module and always stay. A flag that is absent counts as on.
 */
export function dancerMoneyFlow(m: SectionModifiers): NavSection {
  return group({
    i18nKey: 'common:routes.money',
    collapsible: true,
    defaultOpen: true,
    items: [
      ...(m.hasDancerInvoicing !== false
        ? [
            {
              i18nKey: 'common:routes.invoices',
              path: '/home/invoices',
              icon: FileText,
            },
          ]
        : []),
      ...(m.hasReimbursements !== false
        ? [
            {
              i18nKey: 'common:routes.reimbursements',
              path: '/home/reimbursements',
              icon: Receipt,
            },
          ]
        : []),
      { i18nKey: 'common:routes.payouts', path: '/home/payouts', icon: Wallet },
    ],
  });
}

// ── DANCER · BALLEE ─────────────────────────────────────────────────────────

/** Community — discovery, networking (Ballee). Home is NOT here: it leads the
 *  sidebar in the shared primary cluster (Home + Inbox) built by the matrix
 *  (2026-07-13 top-cluster restructure — the lone-Inbox fix). */
export function dancerCommunityBallee(m: SectionModifiers): NavSection {
  const items: NavItemDef[] = [
    // Feed is hidden from non-super-admins until ready (2026-05-01).
    ...(m.isSuperAdmin
      ? [
          {
            i18nKey: 'common:routes.feed',
            path: '/home/community/feed',
            icon: LayoutGrid,
          },
        ]
      : []),
    // "Explore" intentionally has NO nav entry (BAL-392, removed twice:
    // f2dd5eb20 then again after d14271719 re-added it). The discovery
    // entries directly below (Dancers / Organizations / Marketplace) cover
    // it; the /home/explore route page stays live, just unlinked. Guarded
    // by navigation-role-brand.test.ts — do not re-add.
    // Canonical public URLs since 2026-04-28.
    { i18nKey: 'common:routes.dancers', path: '/dancers', icon: UsersRound },
    {
      i18nKey: 'common:routes.organizations',
      path: '/organizations',
      icon: Building2,
    },
    {
      i18nKey: 'common:routes.lists',
      path: '/home/lists',
      icon: Bookmark,
    },
    {
      i18nKey: 'common:routes.bookmarks',
      path: '/home/bookmarks',
      icon: Bookmark,
    },
    {
      i18nKey: 'common:routes.marketplace',
      path: '/marketplace',
      icon: Store,
      isNew: true,
      primary: 2,
    },
    ...(featureFlagsConfig.enableCoaching
      ? [
          {
            i18nKey: 'common:routes.myCoachings',
            path: '/home/coaching',
            icon: Video,
          },
        ]
      : []),
  ];
  return group({
    i18nKey: 'common:routes.community',
    collapsible: true,
    defaultOpen: true,
    items,
  });
}

/** Opportunities — auditions, competitions, offers (Ballee). The dancer's
 *  own slices live as L2 affordances, not sidebar siblings: "My Applications"
 *  is a header link on /auditions; Scholarships is a tab on /home/offers. */
export const dancerOpportunitiesBallee: NavSection = group({
  i18nKey: 'common:routes.opportunities',
  collapsible: true,
  defaultOpen: true,
  items: [
    {
      i18nKey: 'common:routes.auditions',
      path: '/auditions',
      icon: Calendar,
      primary: 3,
    },
    {
      i18nKey: 'common:routes.competitions',
      path: '/competitions',
      icon: Trophy,
    },
    offersItem,
    ...(featureFlagsConfig.enableAmbassadors
      ? [
          {
            i18nKey: 'common:routes.ambassadors',
            path: '/home/ambassadors',
            icon: Megaphone,
          },
        ]
      : []),
  ],
});

// ── DANCER · BOTH BRANDS ────────────────────────────────────────────────────

/** Profile — personal & professional info (both brands). */
export function dancerProfile(m: SectionModifiers): NavSection {
  const items: NavItemDef[] = [
    // "View my profile" leads the group when the dancer has a public handle,
    // linking straight to their own public page (`/<handle>`). Mirrors the
    // partnerCompetitions dynamic-injection pattern. Reuses the existing
    // edit-page header label key.
    ...(m.dancerProfileSlug
      ? [
          {
            i18nKey: 'dancer:profileEdit.viewPublicProfile',
            path: dancerPath(m.dancerProfileSlug),
            icon: Eye,
          },
        ]
      : []),
    {
      i18nKey: 'common:routes.editProfile',
      path: pathsConfig.app.dancerProfileSetup,
      icon: UserCircle2,
    },
    {
      i18nKey: 'common:routes.mediaLibrary',
      path: pathsConfig.app.dancerMedia,
      icon: Images,
    },
    {
      i18nKey: 'common:routes.privacyVisibility',
      path: pathsConfig.app.dancerProfileVisibility,
      icon: Eye,
    },
    // Repertoire hidden from the menu while the page is empty (BAL-394). The
    // /home/repertoire route is intentionally kept; only the nav entry is removed.
    ...(featureFlagsConfig.enableCoaching
      ? [
          {
            i18nKey: 'common:routes.myCoachings',
            path: '/home/coaching',
            icon: Video,
          },
        ]
      : []),
    {
      i18nKey: 'common:routes.contractDetails',
      path: '/home/artist/contract-details',
      icon: FileCheck,
    },
    {
      i18nKey: 'common:routes.availability',
      path: '/home/artist/availability',
      icon: CalendarDays,
    },
    // Performance hidden from the menu while the dancer has no score data
    // (BAL-490), mirroring the isSeller gate and the BAL-394 Repertoire removal.
    // The /home/artist/performance route is intentionally kept for deep links;
    // only the nav entry is data-gated.
    ...(m.hasPerformanceData
      ? [
          {
            i18nKey: 'common:routes.performance',
            path: '/home/artist/performance',
            icon: Activity,
          },
        ]
      : []),
    // Premium-gated inside the page (paywall for non-premium dancers).
    {
      i18nKey: 'common:routes.profileAnalytics',
      path: '/home/artist/analytics',
      icon: BarChart3,
    },
    {
      i18nKey: 'common:routes.legalStatus',
      path: '/home/legal-status',
      icon: Scale,
    },
  ];
  return group({
    i18nKey: 'common:routes.myProfile',
    collapsible: true,
    defaultOpen: true,
    items,
  });
}

/**
 * Selling — the seller's marketplace surfaces (Sales received + own Listings).
 * Composed into the dancer surfaces only when the user has ≥1 listing
 * (SectionModifiers.isSeller), so non-sellers never see it. The buyer-side
 * order history is intentionally NOT here — it is reconciled separately with
 * the Account "Orders" surface (see BAL-434).
 */
export const sellingSection: NavSection = group({
  i18nKey: 'common:routes.selling',
  collapsible: true,
  defaultOpen: false,
  items: [
    { i18nKey: 'common:routes.sales', path: '/home/sales', icon: Package },
    { i18nKey: 'common:routes.listings', path: '/home/listings', icon: Store },
  ],
});

/** Profile-setup-only section (short-circuit while a dancer must finish setup). */
export const profileSetupOnlySection: NavSection = group({
  i18nKey: 'common:routes.myProfile',
  collapsible: false,
  defaultOpen: true,
  items: [
    {
      i18nKey: 'common:routes.completeProfile',
      path: pathsConfig.app.dancerProfileSetup,
      icon: UserCircle2,
      primary: 1,
    },
  ],
});

/**
 * Membership-request-pending notice (BAL-668). The resolver surfaces it only
 * when the user has a pending self-claimed org seat (`hasPendingOrgJoinRequest`)
 * AND holds no other nav role — an informational single item, not a work
 * surface, so the shell explains the wait instead of a bare Home + Inbox. Links
 * to `/home` (the dashboard, where the pending-approval banner lives). Mirrors
 * `profileSetupOnlySection`'s single-leaf, non-collapsible shape; the leaf
 * shares the section key so both render surfaces show it as one bare row.
 */
export const membershipRequestPendingSection: NavSection = group({
  i18nKey: 'common:routes.membershipRequestPending',
  collapsible: false,
  defaultOpen: true,
  items: [
    {
      i18nKey: 'common:routes.membershipRequestPending',
      path: '/home',
      icon: Clock,
    },
  ],
});

// ── INBOX + ACCOUNT (both brands, all authenticated users) ──────────────────

export const inboxSection: NavSection = group({
  i18nKey: 'common:routes.inbox',
  collapsible: false,
  defaultOpen: true,
  items: [
    {
      i18nKey: 'common:routes.inbox',
      path: '/home/inbox',
      icon: Inbox,
      primary: 4,
    },
  ],
});

/**
 * Home link for users whose surface doesn't otherwise lead with one. The
 * dancer cells already open with a Home item inside their first group
 * (Today / Community); pure Ballee org / competition-partner users (the
 * `balleeClientUser` cell) previously led straight into Inbox with no Home.
 * Mirrors `inboxSection`'s single-leaf, non-collapsible shape and points at
 * the same `/home` dashboard the dancer cells use.
 */
export const homeSection: NavSection = group({
  i18nKey: 'common:routes.home',
  collapsible: false,
  defaultOpen: true,
  items: [
    { i18nKey: 'common:routes.home', path: '/home', icon: Home, primary: 1 },
  ],
});

export function accountSection(m: SectionModifiers): NavSection {
  const items: NavItemDef[] = [
    {
      i18nKey: 'common:routes.settings',
      path: pathsConfig.app.personalAccountSettings,
      icon: Settings2,
    },
    {
      i18nKey: 'common:routes.notifications',
      path: '/home/settings/notifications',
      icon: Bell,
    },
    {
      i18nKey: 'common:routes.referrals',
      path: '/home/settings/referrals',
      icon: Gift,
    },
    {
      i18nKey: 'common:routes.managers',
      path: '/home/settings/managers',
      icon: UsersRound,
    },
    {
      i18nKey: 'common:routes.claudeConnection',
      path: '/home/settings/claude-connection',
      icon: KeyRound,
    },
    ...(featureFlagsConfig.enablePersonalAccountBilling && m.canSeeBilling
      ? [
          {
            i18nKey: 'common:routes.billing',
            path: pathsConfig.app.personalAccountBilling,
            icon: CreditCard,
          },
        ]
      : []),
    { i18nKey: 'common:routes.orders', path: '/home/orders', icon: Package },
  ];
  return group({
    i18nKey: 'common:routes.account',
    collapsible: true,
    defaultOpen: false,
    items,
  });
}

// ── CONSOLIDATED PROFILE + ACCOUNT (footer dropdown / mobile sheet) ─────────

/**
 * Plain-scalar flags for building the consolidated "My Profile" / "Account"
 * groups OUTSIDE the matrix (2026-07-05 consolidation: these two groups moved
 * from the sidebar sections into the footer user dropdown and the mobile
 * menu sheet). Scalars only, so a server layout can pass them across the RSC
 * boundary to the client surfaces that render the groups.
 */
export interface ConsolidatedNavFlags {
  isSuperAdmin: boolean;
  isClientUser: boolean;
  isDancer: boolean;
  needsProfileSetup: boolean;
  hasPerformanceData: boolean;
  clientRole: 'owner' | 'member' | null;
  dancerProfileSlug: string | null;
}

/**
 * Client-user self-management (profile setup + team). Moved OUT of the
 * client-portal sidebar groups (Phase A, 2026-07-05 proposal §2): the two
 * 4-segment deep links (`/home/client-user/profile/setup`,
 * `/home/client-user/settings/team`) live in the account-chip dropdown and
 * the mobile sheet — the one legitimate second surface for self-management.
 * The team entry keeps the portal's gate: client owners and super admins.
 */
function clientAccountGroup(flags: ConsolidatedNavFlags): NavSection {
  return group({
    i18nKey: 'account:roleSwitcher.roleLabels.client',
    collapsible: true,
    defaultOpen: false,
    items: [
      {
        i18nKey: 'common:routes.editProfile',
        path: '/home/client-user/profile/setup',
        icon: UserCircle2,
      },
      ...(flags.clientRole === 'owner' || flags.isSuperAdmin
        ? [
            {
              i18nKey: 'common:routes.team',
              path: '/home/client-user/settings/team',
              icon: Users,
            },
          ]
        : []),
    ],
  });
}

/**
 * The ordered group list both consumers render: [My Profile (dancers only,
 * skipped while setup is incomplete), Client (client users only), Account].
 * Single source of truth so the dropdown and the mobile sheet can never
 * drift apart.
 */
/**
 * Drop `/home/*` leaves owned by the other brand from a consolidated group,
 * through the SAME gate the sidebar resolver uses (Critical Rule #8 — no
 * parallel brand list). A group emptied by the filter is dropped. Non-`/home`
 * cross-brand surfaces pass through (the proxy 301/308 handles them).
 */
function filterConsolidatedGroupForBrand(
  section: Extract<NavSection, { kind: 'group' }>,
  brand: Brand,
): Extract<NavSection, { kind: 'group' }> | null {
  const items = section.items.filter((item) =>
    isRouteVisibleForBrand(item.path, brand),
  );
  return items.length > 0 ? { ...section, items } : null;
}

/**
 * @param brand When provided, `/home/*` leaves owned by the other brand are
 *   filtered out. The account dropdown and mobile sheet render on BOTH brands,
 *   so they MUST pass the current brand — otherwise Flow-only settings (client
 *   Edit profile / Team, dancer Contract Details) leak into the Ballee menu
 *   and bounce to Flow (the "settings pages redirect to Flow" bug). Omitting
 *   `brand` preserves the unfiltered list (used only by shape-level tests).
 */
export function consolidatedAccountSections(
  flags: ConsolidatedNavFlags,
  brand?: Brand,
): Extract<NavSection, { kind: 'group' }>[] {
  const modifiers: SectionModifiers = {
    isSuperAdmin: flags.isSuperAdmin,
    isClientUser: flags.isClientUser,
    isDancer: flags.isDancer,
    isOrganizer: false,
    isSeller: false,
    hasPerformanceData: flags.hasPerformanceData,
    clientRole: flags.clientRole,
    // Mirrors resolve-roles.ts: client users see billing only as owners;
    // super admins and non-client users always can.
    canSeeBilling:
      !flags.isClientUser || flags.clientRole === 'owner' || flags.isSuperAdmin,
    partnerCompetitions: [],
    dancerProfileSlug: flags.dancerProfileSlug,
  };

  const sections: NavSection[] = [
    ...(flags.isDancer && !flags.needsProfileSetup
      ? [dancerProfile(modifiers)]
      : []),
    ...(flags.isClientUser ? [clientAccountGroup(flags)] : []),
    accountSection(modifiers),
  ];

  const groups = sections.filter(
    (s): s is Extract<NavSection, { kind: 'group' }> => s.kind === 'group',
  );

  if (!brand) return groups;

  return groups
    .map((g) => filterConsolidatedGroupForBrand(g, brand))
    .filter((g): g is Extract<NavSection, { kind: 'group' }> => g !== null);
}

// ── CLIENT USER · FLOW ──────────────────────────────────────────────────────

/** Client home link (pure client users only — above Inbox). */
export const clientHomeFlow: NavSection = group({
  i18nKey: 'common:routes.home',
  collapsible: false,
  defaultOpen: true,
  items: [
    {
      i18nKey: 'common:routes.home',
      path: '/home/client-user',
      icon: Home,
      primary: 1,
    },
  ],
});

/** The Client Portal group set (Flow) — the `client` acting role's menu
 *  (Phase A, 2026-07-05 proposal §2). Four labeled groups; Home + Inbox lead
 *  as bare items in the clientUser cell. Super admins preview it by acting
 *  as Client, so no special label variant remains. The client user's own
 *  profile/team management lives in consolidatedAccountSections, not here. */
export function clientPortalGroupsFlow(): NavSection[] {
  return [
    // Events — core event and cast management
    group({
      i18nKey: 'common:routes.events',
      collapsible: true,
      defaultOpen: true,
      items: [
        {
          i18nKey: 'common:routes.castSheet',
          path: '/home/client-user/castsheet',
          icon: LayoutGrid,
          primary: 2,
        },
        {
          i18nKey: 'common:routes.events',
          path: '/home/client-user/events',
          icon: CalendarPlus,
          primary: 3,
        },
        {
          i18nKey: 'common:routes.dancers',
          path: '/home/client-user/artists',
          icon: Users,
        },
      ],
    }),
    // Quality — feedback and issue tracking
    group({
      i18nKey: 'common:routes.quality',
      collapsible: true,
      defaultOpen: true,
      items: [
        {
          i18nKey: 'common:routes.feedbacks',
          path: '/home/client-user/feedbacks',
          icon: MessageSquare,
        },
        {
          i18nKey: 'common:routes.issues',
          path: '/home/client-user/issues',
          icon: AlertTriangle,
        },
      ],
    }),
    // Billing — financial documents
    group({
      i18nKey: 'common:routes.billing',
      collapsible: true,
      defaultOpen: true,
      items: [
        {
          i18nKey: 'common:routes.invoices',
          path: '/home/client-user/invoices',
          icon: Receipt,
        },
        {
          i18nKey: 'common:routes.reimbursements',
          path: '/home/client-user/reimbursements',
          icon: Receipt,
        },
        {
          i18nKey: 'common:routes.hireOrders',
          path: '/home/client-user/hire-orders',
          icon: FileText,
        },
      ],
    }),
    // Insights — analytics and reporting
    group({
      i18nKey: 'common:routes.insights',
      collapsible: true,
      defaultOpen: true,
      items: [
        {
          i18nKey: 'common:routes.analytics',
          path: '/home/client-user/analytics',
          icon: BarChart3,
        },
        {
          i18nKey: 'common:routes.artistRankings',
          path: '/home/client-user/artist-analytics',
          icon: Star,
        },
      ],
    }),
  ];
}
